import { mapApiToAppointment, type RawAppointment } from 'core/appointments/infra/appointmentMapper'
import { type RawContainer } from 'core/container-details/domain/ShipmentContainer'
import { mapApiToContainer } from 'core/container-details/infra/ContainerMapper'
import type { CommodityItem } from 'core/domain/CommodityItem'
import { type EntityType } from 'core/domain/EntityType'
import type { EquipmentSubtypes } from 'core/domain/Load'
import { type RawManagedBy } from 'core/domain/ManagedBy'
import { type Mode } from 'core/domain/Mode'
import { type Shipper } from 'core/domain/Shipper'
import { type Stop } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { mapApiToLoadMeasures, type RawLoadMeasures } from 'core/infra/LoadMeasuresMapper'
import { mapApiToManagedBy } from 'core/infra/ManagedByMapper.ts'
import { type RawItem } from 'core/shipping-items/domain/RawShippingItems'
import {
  mapApiToShippingItems,
  mapApiToShippingMeasures,
} from 'core/shipping-items/infra/ShippingItemsMapper'
import { createDate } from 'core/utils/date'
import { parseNumber } from 'core/utils/number'

import { type Load } from '../../domain/Load'
import { type TenderRateType } from '../../domain/Tender/TenderRateType'
import { type TenderStatus } from '../../domain/Tender/TenderStatus'

export type RawShipmentMeasures = {
  total_weight: number | string
  total_volume: number | string
  density: number | string
  total_linear_feet: number | string
}

export type RawShipper = {
  company_name: string
  email: string
  logo_email_file: string | null
  name: string | null
  phone_number: string | null
}

export type RawStop = {
  address?: string | null
  city: string
  country?: string | null
  date?: string | null
  latitude?: string | null
  longitude?: string | null
  state: string
  stop_index: number
  zipcode?: string | null
  facility?: { company_name?: string } | null
  appointment: RawAppointment | null
}

export type RawEquipmentRequirements = {
  equipment_subtypes: Array<EquipmentSubtypes>
  temperature?: string | null
  unit?: string | null
}

export type RawTender = {
  carrier: {
    id: number
    name: string
    email: string
    entity_type: EntityType | null
  }
  carrier_1xp_link: string | null
  created_at: string
  equipment_type: {
    label: string
    value: TruckType
  }
  flat_rate: string
  items: Array<RawItem>
  measures: RawLoadMeasures
  mileage: number
  mode: Mode
  notes: string | null
  quote_created_at: string
  rate: {
    contracted_lane: {
      award_type: string
      carrier: {
        id: string
      }
      carrier_invite_uuid: string | null
      channel: string
      contract: {
        id: string
        title: string
      }
      effective_date: string
      equipment_type: TruckType
      expiration_date: string
      flat_rate: string
      id: string
      lane_proposal: {
        id: string
      } | null
      max_weight: string | null
      min_weight: string
      minimum_charge: string | null
      notes: string
      per_mile_rate: string | null
      rate_type: string
      reference_number: string
      status: string
      stops: Array<{
        city: string
        country: string
        state: string
        zipcode: string
      }>
      volume: number
    } | null
    uuid: string | null
    provider_rate_id: string | null
  }
  rate_type: TenderRateType
  reply_deadline: string
  shipper: RawShipper
  shipper_guide_id: string
  status: TenderStatus
  stops: Array<RawStop>
  uuid: string
  equipment_requirements: RawEquipmentRequirements | null
  share_details: boolean
  power_only: boolean
  attachments: Array<{
    id: number
    file_name: string
  }>
  managed_by: RawManagedBy
  quote_uuid: string
  container?: RawContainer
}

function mapApiToItems(items?: RawItem[] | null): CommodityItem[] {
  return (items ?? []).map(item => ({
    commodity: item.commodity,
    freightClass: item.freight_class,
    height: parseNumber(item.height),
    length: parseNumber(item.length),
    nmfcCode: item.nmfc_code,
    packageCount: parseNumber(item.package_count),
    packageType: item.package_type,
    stackable: item.stackable,
    turnable: item.turnable,
    weight: parseNumber(item.weight),
    width: parseNumber(item.width),
  }))
}

export function mapApiToShipper(rawShipper: RawShipper): Shipper {
  return {
    companyName: rawShipper.company_name,
    email: rawShipper.email,
    logoUrl: rawShipper.logo_email_file || undefined,
    name: rawShipper.name || undefined,
    phoneNumber: rawShipper.phone_number || undefined,
  }
}

export function mapApiToStop(rawStop: RawStop): Stop {
  return {
    address: rawStop.address || undefined,
    city: rawStop.city,
    country: rawStop.country || undefined,
    date: rawStop.date || undefined,
    latitude: rawStop.latitude || undefined,
    longitude: rawStop.longitude || undefined,
    state: rawStop.state,
    stopIndex: rawStop.stop_index,
    zipCode: rawStop.zipcode || undefined,
    facilityName: rawStop.facility?.company_name || undefined,
    appointment: mapApiToAppointment(rawStop.appointment),
  }
}

export function mapApiToEquipmentRequirements(equipmentRequirements?: RawEquipmentRequirements) {
  return {
    temperature: equipmentRequirements?.temperature || undefined,
    temperatureUnit: equipmentRequirements?.unit || undefined,
  }
}

export function mapApiToEquipmentSubtypes(equipmentRequirements?: RawEquipmentRequirements) {
  return equipmentRequirements?.equipment_subtypes || undefined
}

export function mapApiToAttachment(attachment: { id: number; file_name: string }) {
  return {
    id: attachment.id,
    fileName: attachment.file_name,
  }
}

export function mapApiToLoad(rawTender: RawTender): Load {
  return {
    carrier: {
      name: rawTender.carrier.name,
      entityType: rawTender.carrier.entity_type ?? undefined,
    },
    carrier1xpLink: rawTender.carrier_1xp_link || undefined,
    contract:
      rawTender.rate.contracted_lane != null
        ? {
            id: rawTender.rate.contracted_lane.contract.id,
            title: rawTender.rate.contracted_lane.contract.title,
            rfp: {
              inviteUUID: rawTender.rate.contracted_lane.carrier_invite_uuid ?? undefined,
            },
          }
        : undefined,
    container: mapApiToContainer(rawTender.container),
    createdAt: createDate(rawTender.created_at),
    deadline: createDate(rawTender.reply_deadline),
    equipmentType: rawTender.equipment_type.value,
    flatRate: rawTender.flat_rate,
    id: rawTender.uuid,
    items: mapApiToItems(rawTender.items),
    shippingItems: mapApiToShippingItems(rawTender.items),
    shippingMeasures: mapApiToShippingMeasures(rawTender.measures),
    mileage: rawTender.mileage,
    mode: rawTender.mode,
    powerOnly: rawTender.power_only,
    notes: rawTender.notes || undefined,
    shipper: mapApiToShipper(rawTender.shipper),
    stops: rawTender.stops.map(mapApiToStop),
    tender: {
      rateType: rawTender.rate_type,
      status: rawTender.status,
    },
    type: 'tender',
    equipmentRequirements: mapApiToEquipmentRequirements(
      rawTender.equipment_requirements ?? undefined
    ),
    equipmentSubtypes: mapApiToEquipmentSubtypes(rawTender.equipment_requirements ?? undefined),
    shareDetails: rawTender.share_details,
    rateUUID: rawTender.rate.uuid ?? undefined,
    measures: mapApiToLoadMeasures(rawTender.measures),
    managedBy: mapApiToManagedBy(rawTender.managed_by),
    quoteUUID: rawTender.quote_uuid,
    attachments: rawTender.attachments.map(mapApiToAttachment),
    providerRateId: rawTender.rate.provider_rate_id ?? undefined,
  }
}
